import React, { useRef, useEffect, useCallback } from 'react';
import InputBox from '../FormComponent/InputBox';
import { createQuickFilterAST, QUICK_FILTER_FIELDS_BY_ENTITY } from '../../utils/query/quickFilterPattern';
import styles from './QuickSearchInput.module.scss';

const DEFAULT_DEBOUNCE_MS = 1000;

/**
 * Reusable quick-search input. Emits a quick-filter AST (OR of contains over entity fields)
 * after debounce, or null when empty. Use for Project, Test Group, or Testcase list pages.
 *
 * @param {string} value - Controlled input value
 * @param {function} onChange - Immediate handler: (value: string) => void
 * @param {function} onSearchChange - Debounced: (ast: object | null) => void
 * @param {'project'|'testgroup'|'testcase'} entityType - Determines which fields are used (name/title, key, description)
 * @param {string} [placeholder] - Placeholder text (default: "Search...")
 * @param {number} [debounceMs] - Debounce delay in ms (default: 500)
 * @param {string} [size] - Input size: 'small' | 'medium' (default: 'small')
 * @param {number} [minWidth] - Minimum width in px (default: 0, use parent maxWidth to size)
  * @param {boolean} [disabled] - Disable input
  */
 function QuickSearchInput({
   value = '',
   onChange,
   onSearchChange,
   entityType = 'testcase',
   placeholder = 'Search...',
   debounceMs = DEFAULT_DEBOUNCE_MS,
   size = 'small',
   minWidth = 0,
   disabled = false
 }) {
  const timeoutRef = useRef(null);

  const fields = QUICK_FILTER_FIELDS_BY_ENTITY[entityType] ?? QUICK_FILTER_FIELDS_BY_ENTITY.testcase;

  const commitSearch = useCallback(
    (rawValue) => {
      const trimmed = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (trimmed === '') {
        onSearchChange?.(null);
      } else {
        const ast = createQuickFilterAST(trimmed, fields);
        onSearchChange?.(ast);
      }
    },
    [onSearchChange, fields]
  );

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const raw = typeof value === 'string' ? value : '';
    if (raw.trim() === '') {
      commitSearch('');
      return;
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      commitSearch(raw);
    }, debounceMs);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, debounceMs, commitSearch]);

  const handleChange = useCallback(
    (e) => {
      const next = e?.target?.value ?? '';
      onChange?.(next);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange?.('');
  }, [onChange]);

  const showClearLink = typeof value === 'string' && value.trim().length > 0;

  const clearLinkStyle = {
    cursor: 'pointer',
    fontSize: 12,
    color: '#475569',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ width: '100%', minWidth }}>
      <InputBox
        size={size}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        showValidationIcon={false}
        customClassName={showClearLink ? styles.inputWithClear : ''}
        endIcon={showClearLink ? <span style={clearLinkStyle}>Clear</span> : undefined}
        onEndIconClick={showClearLink ? handleClear : undefined}
      />
    </div>
  );
}

export default QuickSearchInput;
